// ── Employee Performance widget: data contract, columns, settings and sorting ──
//
// Everything the widget, its column editor and the three dashboard pages share lives here so the
// rules (which columns exist, what a saved layout may contain, how rows sort) are pure and tested.
// Figures come from GET /api/dashboard/employee-performance, which applies the dashboard's store,
// channel, NET/GROSS and multibuy filters exactly as the KPI cards do — so the Sales total here
// equals the Net Sales card for the same view.

import { API_BASE } from './api'

// ── API contract ─────────────────────────────────────────────────────

export interface EmployeePerformanceFigures {
    transactions: number
    units: number
    /** After line discounts — the dashboard's Net Sales (Gross Sales when returns are excluded). */
    netSales: number
    /** Before line discounts: Σ Retail × Qty. */
    grossRetail: number
    /** Σ ItemDiscount × Qty — every line discount: staffGivenDiscounts + promotionalDiscounts. */
    discounts: number
    /**
     * The discount on lines that carry a discount reason — keyed in by a staff member, who had to
     * pick one (any reason, Sale included). Absent from a server older than the split, hence optional.
     */
    staffGivenDiscounts?: number
    /** The discount on lines with no discount reason — prices the till applied itself. Optional, as above. */
    promotionalDiscounts?: number
    /** Adventure Bucks credited on the transactions this employee rang. */
    bucksIssued: number
    /**
     * Adventure Bucks the employee credited by hand (ad-hoc grants and retroactive credits applied
     * at the till), by when and where they were applied. Null unless the request asked for it
     * (includeManualGrants), so "not asked for" can never pass for $0.00 — and absent from a server
     * older than the column, hence optional.
     */
    manualGrants?: number | null
}

export interface EmployeePerformanceRow extends EmployeePerformanceFigures {
    employeeId: string
    name: string
    /** Legacy store GUID of the employee's own store (not necessarily where the sale rang). */
    homeStoreId: string | null
    homeStoreName: string | null
    /** The employee record is flagged deleted — they have left, but still rang these sales. */
    departed: boolean
}

export interface EmployeePerformanceData {
    /** Inclusive yyyy-MM-dd bounds of the window the server actually queried. */
    startDate: string
    endDate: string
    employees: EmployeePerformanceRow[]
    totals: EmployeePerformanceFigures
}

export type EmployeePerformancePeriod = 'daily' | 'monthly' | 'custom'

export interface EmployeePerformanceFilters {
    storeId: string
    channel: string
    includeReturns: boolean
    includeMultibuys: boolean
    /** daily: the day (yyyy-MM-dd). monthly: any day in the month (yyyy-MM-01). */
    date?: string
    /** custom: inclusive range (yyyy-MM-dd). */
    startDate?: string
    endDate?: string
    /** Ask for the Manual grants figures — a second, costlier query, so only while the column shows. */
    includeManualGrants?: boolean
}

/**
 * What a page hands both the widget's query and the widget itself: the period and filters the
 * figures are asked for with, and the page's main query key (so its Refresh reloads them). The
 * drill-down asks for an employee's receipts with exactly these, so it covers the same view.
 */
export interface EmployeePerformanceQueryContext {
    period: EmployeePerformancePeriod
    filters: EmployeePerformanceFilters
    /** Exactly the first element of the page's main dashboard query key. */
    queryKeyPrefix: string
}

/** The dashboard parameters both Employee Performance endpoints take, in the order the widget sends them. */
export function employeePerformanceParams(period: EmployeePerformancePeriod, f: EmployeePerformanceFilters): URLSearchParams {
    const p = new URLSearchParams({
        period,
        storeId: f.storeId,
        channel: f.channel,
        includeReturns: String(f.includeReturns),
        includeMultibuys: String(f.includeMultibuys),
    })
    if (period === 'custom') {
        if (f.startDate) p.set('startDate', f.startDate)
        if (f.endDate) p.set('endDate', f.endDate)
    } else if (f.date) {
        p.set('date', f.date)
    }
    return p
}

export function buildEmployeePerformanceUrl(period: EmployeePerformancePeriod, f: EmployeePerformanceFilters): string {
    const p = employeePerformanceParams(period, f)
    if (f.includeManualGrants) p.set('includeManualGrants', 'true')
    return `/api/dashboard/employee-performance?${p.toString()}`
}

/** How the dashboards' store selector reads: a store's name, "My Store Group" or "All Stores". */
export function storeSelectionLabel(storeId: string, stores: readonly { id: string; name: string }[] | undefined): string {
    if (storeId === 'all') return 'All Stores'
    if (storeId === 'group') return 'My Store Group'
    return stores?.find(s => s.id.toLowerCase() === storeId.toLowerCase())?.name ?? 'Selected store'
}

/** A failed fetch that keeps the HTTP status, so the widget can tell "not allowed" from "broken". */
export class EmployeePerformanceError extends Error {
    readonly status: number
    constructor(status: number, message: string) {
        super(message)
        this.name = 'EmployeePerformanceError'
        this.status = status
    }
}

/** The user-facing sentence for a failed load; never echoes server internals. */
export function describeEmployeePerformanceError(err: unknown): string {
    if (err instanceof EmployeePerformanceError) {
        if (err.status === 403) return err.message || "You don't have access to employee figures for this selection."
        if (err.status === 400) return err.message || 'These filters can’t be shown as employee figures.'
    }
    return 'Employee figures could not be loaded. Try refreshing.'
}

/** GETs one of the widget's endpoints; a failure keeps its status and the server's own sentence. */
export async function fetchEmployeeJson<T>(url: string, token: string | null): Promise<T> {
    const r = await fetch(API_BASE + url, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } })
    if (!r.ok) {
        let message = ''
        try {
            const body = await r.json()
            if (body && typeof body.error === 'string') message = body.error
        } catch { /* non-JSON body — keep the generic sentence */ }
        throw new EmployeePerformanceError(r.status, message)
    }
    return r.json()
}

export function fetchEmployeePerformance(url: string, token: string | null): Promise<EmployeePerformanceData> {
    return fetchEmployeeJson<EmployeePerformanceData>(url, token)
}

// ── Placement ────────────────────────────────────────────────────────

/**
 * Grid rows the widget takes on a phone (the xs breakpoint). Other widgets get 10 there, but at 10
 * (190px) the sticky header and totals rows leave room for less than one employee; 24 (470px)
 * shows about seven. The layout stores and both grids size it from this.
 */
export const EMPLOYEE_PERFORMANCE_XS_ROWS = 24

// ── Columns ──────────────────────────────────────────────────────────

export type EmployeeColumnKey =
    | 'rank' | 'employee' | 'sales' | 'discounts' | 'bucks' | 'manualGrants' | 'bucksRate'
    | 'discountPct' | 'staffDiscounts' | 'staffDiscountPct' | 'promoDiscounts'
    | 'transactions' | 'avgSale' | 'units' | 'upt' | 'salesShare'

export type EmployeeColumnFormat = 'text' | 'currency' | 'currencyCents' | 'int' | 'decimal' | 'percent'

export interface EmployeeColumnContext {
    /** NET (true) or GROSS (false) — renames the sales column the way the Net Sales card is renamed. */
    includeReturns: boolean
}

export interface EmployeeColumnDef {
    key: EmployeeColumnKey
    /** Short header text. */
    label: (ctx: EmployeeColumnContext) => string
    /** Longer name used in the column editor. */
    menuLabel: (ctx: EmployeeColumnContext) => string
    /** Heading in a copied or exported table, with its unit: the cells there are bare numbers. */
    exportHeader: (ctx: EmployeeColumnContext) => string
    /** Header tooltip / editor hint: what the number is and how it is worked out. */
    description: string
    numeric: boolean
    format: EmployeeColumnFormat
    /** Shown when a layout has never configured the widget. */
    defaultVisible: boolean
    /** Direction a first click (or the editor's sort toggle) sorts in. */
    defaultDir: SortDir
    sortable: boolean
    /** Can't be hidden (the table is meaningless without it). */
    lockedVisible?: boolean
}

export type SortDir = 'asc' | 'desc'

const salesName = (ctx: EmployeeColumnContext) => (ctx.includeReturns ? 'Net Sales' : 'Gross Sales')

/** A Bucks per $100 figure above this multiple of the median employee's is marked as unusual. */
export const BUCKS_RATE_OUTLIER_MULTIPLE = 2

/** Canonical order — also the default display order. */
export const EMPLOYEE_COLUMNS: readonly EmployeeColumnDef[] = [
    {
        key: 'rank', label: () => '#', menuLabel: () => 'Rank', exportHeader: () => 'Rank',
        description: 'Position in the current sort order.',
        numeric: false, format: 'text', defaultVisible: true, defaultDir: 'asc', sortable: false,
    },
    {
        key: 'employee', label: () => 'Employee', menuLabel: () => 'Employee', exportHeader: () => 'Employee',
        description: 'The employee who rang the sale.',
        numeric: false, format: 'text', defaultVisible: true, defaultDir: 'asc', sortable: true, lockedVisible: true,
    },
    {
        key: 'sales', label: salesName, menuLabel: salesName, exportHeader: ctx => `${salesName(ctx)} ($)`,
        description: 'Sales after line discounts, before tax — the same figure as the Net Sales card. With GROSS selected, returns are left out.',
        numeric: true, format: 'currency', defaultVisible: true, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'discounts', label: () => 'Discounts', menuLabel: () => 'Total discounts', exportHeader: () => 'Discounts ($)',
        description: 'Every line discount on the employee’s sales (discount × quantity): Staff-given + Promotional.',
        numeric: true, format: 'currency', defaultVisible: true, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'bucks', label: () => 'Adv. Bucks', menuLabel: () => 'Adventure Bucks issued', exportHeader: () => 'Adventure Bucks Issued ($)',
        description: 'Adventure Bucks credited to customers on the sales this employee rang.',
        numeric: true, format: 'currencyCents', defaultVisible: true, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'manualGrants', label: () => 'Manual Grants', menuLabel: () => 'Adventure Bucks granted by hand',
        exportHeader: () => 'Manual Grants ($)',
        description: 'Adventure Bucks this employee credited by hand — ad-hoc grants and retroactive credits applied at the till — '
            + 'dated when applied, at the store where they were entered (or the store of the sale, where no store was recorded). '
            + 'Only the date and store filters apply. '
            + 'A retroactive credit also counts under Adv. Bucks for whoever rang the original sale.',
        numeric: true, format: 'currencyCents', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'bucksRate', label: () => 'Bucks/$100', menuLabel: () => 'Bucks per $100 sales',
        exportHeader: () => 'Bucks per $100 Sales ($)',
        description: 'Adventure Bucks issued for every $100 of sales: Adv. Bucks ÷ Sales × 100. A dash where there are no sales '
            + `to measure against. ▲ marks a rate more than ${BUCKS_RATE_OUTLIER_MULTIPLE}× the median employee’s in this view.`,
        numeric: true, format: 'currencyCents', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'discountPct', label: () => 'Disc. %', menuLabel: () => 'Discount depth', exportHeader: () => 'Discount Depth (%)',
        description: 'Discounts as a share of the price before discount: Discounts ÷ (Sales + Discounts).',
        numeric: true, format: 'percent', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    // The split of Discounts. Placed after Disc. % — not beside Discounts — so a new layout shows the
    // two visible ones where an older saved layout gets them appended: after Adv. Bucks.
    {
        key: 'staffDiscounts', label: () => 'Staff-given', menuLabel: () => 'Staff-given discounts',
        exportHeader: () => 'Staff-given Discounts ($)',
        description: 'Line discounts with a discount reason recorded: a staff member keyed the discount in at the till '
            + 'and picked a reason. Every reason counts, Sale and Clearance included. Staff-given + Promotional = Discounts.',
        numeric: true, format: 'currency', defaultVisible: true, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'staffDiscountPct', label: () => 'Staff-given %', menuLabel: () => 'Staff-given % of pre-discount sales',
        exportHeader: () => 'Staff-given Discount Depth (%)',
        description: 'Staff-given discounts as a share of the price before discount: Staff-given ÷ (Sales + Discounts), '
            + 'the same base as Disc. %.',
        numeric: true, format: 'percent', defaultVisible: true, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'promoDiscounts', label: () => 'Promotional', menuLabel: () => 'Promotional discounts',
        exportHeader: () => 'Promotional Discounts ($)',
        description: 'Line discounts with no discount reason recorded: prices the till applied itself — clearance and sale '
            + 'prices, top-down sale events, multibuy and eStore pricing. Staff-given + Promotional = Discounts.',
        numeric: true, format: 'currency', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'transactions', label: () => 'Txns', menuLabel: () => 'Transactions', exportHeader: () => 'Transactions',
        description: 'Receipts the employee rang.',
        numeric: true, format: 'int', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'avgSale', label: () => 'Avg Sale', menuLabel: () => 'Average sale', exportHeader: () => 'Average Sale ($)',
        description: 'Sales ÷ Transactions.',
        numeric: true, format: 'currency', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'units', label: () => 'Units', menuLabel: () => 'Units sold', exportHeader: () => 'Units',
        description: 'Items sold; returned items count as negative units when returns are included.',
        numeric: true, format: 'int', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'upt', label: () => 'UPT', menuLabel: () => 'Units per transaction', exportHeader: () => 'Units per Transaction',
        description: 'Units ÷ Transactions.',
        numeric: true, format: 'decimal', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
    {
        key: 'salesShare', label: () => '% of Sales', menuLabel: () => 'Share of sales', exportHeader: () => 'Share of Sales (%)',
        description: 'The employee’s share of all sales in this view.',
        numeric: true, format: 'percent', defaultVisible: false, defaultDir: 'desc', sortable: true,
    },
]

const COLUMN_BY_KEY = new Map(EMPLOYEE_COLUMNS.map(c => [c.key, c]))

export function getEmployeeColumn(key: EmployeeColumnKey): EmployeeColumnDef {
    return COLUMN_BY_KEY.get(key)!
}

export function isEmployeeColumnKey(key: unknown): key is EmployeeColumnKey {
    return typeof key === 'string' && COLUMN_BY_KEY.has(key as EmployeeColumnKey)
}

// ── Settings (persisted inside the dashboard layout's widgetSettings) ──

export interface EmployeeColumnConfig {
    key: EmployeeColumnKey
    visible: boolean
}

/** The widget's slice of WidgetSettings. Every field is optional: layouts saved before a field
 *  existed must still render, so readers go through {@link resolveEmployeeSettings}. */
export interface EmployeePerformanceSettings {
    /** Display order and visibility. Array order is column order. */
    employeeColumns?: EmployeeColumnConfig[]
    employeeSortKey?: EmployeeColumnKey
    employeeSortDir?: SortDir
    /** Footer row with the totals for every employee in the view. Default on. */
    employeeShowTotals?: boolean
    /** Thin bar under each sales figure, scaled to the top seller. Default on. */
    employeeShowBars?: boolean
    /** Show only the first N rows of the sort; 0 or absent shows everyone. */
    employeeRowLimit?: number
}

export const EMPLOYEE_ROW_LIMITS = [0, 5, 10, 25] as const

export const DEFAULT_EMPLOYEE_SORT: { key: EmployeeColumnKey; dir: SortDir } = { key: 'sales', dir: 'desc' }

export function defaultEmployeeColumns(): EmployeeColumnConfig[] {
    return EMPLOYEE_COLUMNS.map(c => ({ key: c.key, visible: c.defaultVisible }))
}

/**
 * Reconciles a saved column list with the canonical one: unknown and duplicate keys are dropped,
 * columns the saved list has never heard of are appended (with their default visibility), and a
 * column that can't be hidden is forced visible. Always returns a fresh array.
 */
export function normalizeEmployeeColumns(saved: unknown): EmployeeColumnConfig[] {
    if (!Array.isArray(saved) || saved.length === 0) return defaultEmployeeColumns()
    const seen = new Set<EmployeeColumnKey>()
    const out: EmployeeColumnConfig[] = []
    for (const item of saved) {
        const key = (item as { key?: unknown })?.key
        if (!isEmployeeColumnKey(key) || seen.has(key)) continue
        seen.add(key)
        const def = getEmployeeColumn(key)
        out.push({ key, visible: def.lockedVisible ? true : (item as { visible?: unknown }).visible !== false })
    }
    for (const def of EMPLOYEE_COLUMNS) {
        if (!seen.has(def.key)) out.push({ key: def.key, visible: def.defaultVisible })
    }
    return out
}

export interface ResolvedEmployeeSettings {
    columns: EmployeeColumnConfig[]
    sortKey: EmployeeColumnKey
    sortDir: SortDir
    showTotals: boolean
    showBars: boolean
    rowLimit: number
}

/** Every field filled in, whatever the saved layout held. */
export function resolveEmployeeSettings(s: EmployeePerformanceSettings | undefined | null): ResolvedEmployeeSettings {
    const columns = normalizeEmployeeColumns(s?.employeeColumns)
    const savedKeyUsable = isEmployeeColumnKey(s?.employeeSortKey) && getEmployeeColumn(s!.employeeSortKey!).sortable
    const sortKey = savedKeyUsable ? s!.employeeSortKey! : DEFAULT_EMPLOYEE_SORT.key
    // A saved direction belongs to the saved column: when that column is unknown or can't sort, the
    // fallback column takes its own default direction rather than inheriting a stranger's.
    const sortDir: SortDir = savedKeyUsable && (s?.employeeSortDir === 'asc' || s?.employeeSortDir === 'desc')
        ? s.employeeSortDir
        : (sortKey === DEFAULT_EMPLOYEE_SORT.key ? DEFAULT_EMPLOYEE_SORT.dir : getEmployeeColumn(sortKey).defaultDir)
    const limit = Number(s?.employeeRowLimit)
    return {
        columns,
        sortKey,
        sortDir,
        showTotals: s?.employeeShowTotals !== false,
        showBars: s?.employeeShowBars !== false,
        rowLimit: Number.isInteger(limit) && limit > 0 ? limit : 0,
    }
}

/** The settings patch that stores a resolved configuration (plain JSON — safe for layout saves). */
export function toEmployeeSettingsPatch(r: ResolvedEmployeeSettings): Required<EmployeePerformanceSettings> {
    return {
        employeeColumns: r.columns.map(c => ({ key: c.key, visible: c.visible })),
        employeeSortKey: r.sortKey,
        employeeSortDir: r.sortDir,
        employeeShowTotals: r.showTotals,
        employeeShowBars: r.showBars,
        employeeRowLimit: r.rowLimit,
    }
}

export function visibleEmployeeColumns(columns: EmployeeColumnConfig[]): EmployeeColumnDef[] {
    return columns.filter(c => c.visible).map(c => getEmployeeColumn(c.key))
}

/** Whether the widget shows the Manual grants column — the pages request those figures only then. */
export function employeeWantsManualGrants(settings: EmployeePerformanceSettings | undefined | null): boolean {
    return resolveEmployeeSettings(settings).columns.some(c => c.key === 'manualGrants' && c.visible)
}

/** Moves the column at `from` to index `to` (both clamped); returns a new array. */
export function moveEmployeeColumn(columns: EmployeeColumnConfig[], from: number, to: number): EmployeeColumnConfig[] {
    if (from < 0 || from >= columns.length) return columns.slice()
    const target = Math.max(0, Math.min(columns.length - 1, to))
    const next = columns.slice()
    const [item] = next.splice(from, 1)
    next.splice(target, 0, item)
    return next
}

/** Shows/hides a column; a column that can't be hidden is left as it is. */
export function setEmployeeColumnVisible(columns: EmployeeColumnConfig[], key: EmployeeColumnKey, visible: boolean): EmployeeColumnConfig[] {
    if (getEmployeeColumn(key).lockedVisible) return columns.slice()
    return columns.map(c => (c.key === key ? { ...c, visible } : c))
}

/** Header-click / sort-toggle semantics: the active column flips, any other column starts at its default. */
export function nextEmployeeSort(current: { key: EmployeeColumnKey; dir: SortDir }, key: EmployeeColumnKey): { key: EmployeeColumnKey; dir: SortDir } {
    if (!getEmployeeColumn(key).sortable) return current
    if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
    return { key, dir: getEmployeeColumn(key).defaultDir }
}

// ── Values ───────────────────────────────────────────────────────────

const ratio = (num: number, den: number) => (den > 0 ? num / den : 0)

export function discountDepth(f: Pick<EmployeePerformanceFigures, 'netSales' | 'discounts'>): number {
    return ratio(f.discounts, f.netSales + f.discounts)
}

/** Staff-given discounts over the price before discount — Disc. %'s base — or null from a server older than the split. */
export function staffGivenDepth(f: Pick<EmployeePerformanceFigures, 'netSales' | 'discounts' | 'staffGivenDiscounts'>): number | null {
    return f.staffGivenDiscounts == null ? null : ratio(f.staffGivenDiscounts, f.netSales + f.discounts)
}

/**
 * Adventure Bucks issued per $100 of sales, or null where there are no sales to measure against —
 * none, or fewer than returned under NET. Unlike the averages this is not 0 there: bucks credited
 * against no sales are not a rate of zero, and a zero would rank the row as the lowest rate.
 */
export function bucksPer100(f: Pick<EmployeePerformanceFigures, 'bucksIssued' | 'netSales'>): number | null {
    if (!(f.netSales > 0)) return null
    const rate = (f.bucksIssued * 100) / f.netSales
    return Number.isFinite(rate) ? rate : null
}

/** The numeric value a column shows for a set of figures (a row or the totals), or null for text columns. */
export function employeeColumnValue(key: EmployeeColumnKey, f: EmployeePerformanceFigures, totals: EmployeePerformanceFigures): number | null {
    switch (key) {
        case 'sales': return f.netSales
        case 'discounts': return f.discounts
        case 'bucks': return f.bucksIssued
        // Not requested (or an older server): no number, so the cell shows a dash rather than $0.00.
        case 'manualGrants': return f.manualGrants ?? null
        case 'bucksRate': return bucksPer100(f)
        case 'discountPct': return discountDepth(f)
        // A server older than the split sends neither figure: a dash, never a $0 that reads as "none given".
        case 'staffDiscounts': return f.staffGivenDiscounts ?? null
        case 'staffDiscountPct': return staffGivenDepth(f)
        case 'promoDiscounts': return f.promotionalDiscounts ?? null
        case 'transactions': return f.transactions
        case 'avgSale': return ratio(f.netSales, f.transactions)
        case 'units': return f.units
        case 'upt': return ratio(f.units, f.transactions)
        case 'salesShare': return totals.netSales !== 0 ? f.netSales / totals.netSales : 0
        default: return null
    }
}

const currencyCents = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
// signDisplay 'negative' drops the sign from a value that rounds to zero: a return of −$0.40, or
// a −0.04% staff-given share, reads $0 / 0.0%, never −$0 / −0.0%.
const currencyWhole = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0, signDisplay: 'negative' })
const intFmt = new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 })
const decimalFmt = new Intl.NumberFormat('en-CA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
const percentFmt = new Intl.NumberFormat('en-CA', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1, signDisplay: 'negative' })

export function formatEmployeeValue(format: EmployeeColumnFormat, value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '—'
    switch (format) {
        case 'currency': return currencyWhole.format(value)
        case 'currencyCents': return currencyCents.format(value)
        case 'int': return intFmt.format(value)
        case 'decimal': return decimalFmt.format(value)
        case 'percent': return percentFmt.format(value)
        default: return String(value)
    }
}

// ── Sorting ──────────────────────────────────────────────────────────

const nameCollator = new Intl.Collator('en-CA', { sensitivity: 'base', numeric: true })

/**
 * Sorts a copy of the rows. Ties (and the text column) fall back to name, then employeeId, so the
 * order is stable across refreshes and never depends on the server's row order. A row with no
 * figure in the column (a rate over no sales; Manual grants not asked for) sorts after every row
 * that has one, whichever way the column sorts — it is neither the highest nor the lowest.
 */
export function sortEmployees(
    rows: readonly EmployeePerformanceRow[],
    key: EmployeeColumnKey,
    dir: SortDir,
    totals: EmployeePerformanceFigures,
): EmployeePerformanceRow[] {
    const sign = dir === 'asc' ? 1 : -1
    const byName = (a: EmployeePerformanceRow, b: EmployeePerformanceRow) =>
        nameCollator.compare(a.name, b.name) || (a.employeeId < b.employeeId ? -1 : a.employeeId > b.employeeId ? 1 : 0)
    return rows.slice().sort((a, b) => {
        if (key === 'employee' || key === 'rank') return sign * byName(a, b)
        const va = employeeColumnValue(key, a, totals)
        const vb = employeeColumnValue(key, b, totals)
        if (va === null || vb === null) {
            if (va !== vb) return va === null ? 1 : -1
        } else if (va !== vb) {
            return sign * (va < vb ? -1 : 1)
        }
        return byName(a, b)
    })
}

// ── Bucks per $100: the outlier marker ───────────────────────────────

/** Whole cents: the precision the column and its legend show. */
const cents = (v: number) => Math.round(v * 100)

/**
 * The median Bucks per $100 across every employee in the view who has a rate: the baseline the
 * outlier marker measures against. The median rather than the totals row's rate, because online
 * orders earn no bucks at the till — under All channels they drag the sales-weighted total down and
 * would make ordinary in-store staff look high. Null when nobody has a rate, and when the median
 * comes to $0.00: no multiple of nothing means anything, and every rate of a cent would be marked.
 */
export function bucksRateMedian(rows: readonly EmployeePerformanceFigures[]): number | null {
    const rates = rows.map(bucksPer100).filter((r): r is number => r !== null).sort((a, b) => a - b)
    if (rates.length === 0) return null
    const mid = Math.floor(rates.length / 2)
    const median = rates.length % 2 === 1 ? rates[mid] : (rates[mid - 1] + rates[mid]) / 2
    return cents(median) > 0 ? median : null
}

/**
 * Whether a row's Bucks per $100 is more than {@link BUCKS_RATE_OUTLIER_MULTIPLE}× the view's median.
 * Compared in the whole cents on screen, so the marker always agrees with the figures a reader can
 * check — $3.02 is never "more than twice" a median shown as $1.51 — and floating-point noise can't
 * tip a rate of exactly twice the median over.
 */
export function isBucksRateOutlier(f: EmployeePerformanceFigures, median: number | null): boolean {
    const rate = bucksPer100(f)
    return rate !== null && median !== null && cents(rate) > BUCKS_RATE_OUTLIER_MULTIPLE * cents(median)
}

// ── The way into the Adventure Bucks audit ───────────────────────────

/** Opens /reports/adventure-bucks. SuperAdmin-only by default, and handed out per person. */
export const LOYALTY_AUDIT_PERMISSION = 'loyalty.audit.view'

/** The id the server gives the row of sales that recorded no employee ("Unknown employee"). */
const NO_EMPLOYEE_ID = '00000000-0000-0000-0000-000000000000'

/**
 * Whether the audit shows this person every store. It scopes with ChainWideFlagScope.None: only a
 * role of HeadOffice or above opens All stores there. The ViewChainWideSales flag that opens All
 * stores on the dashboards deliberately does not widen an employee-conduct audit.
 */
export function canAuditChainWide(role: string | null | undefined): boolean {
    return role === 'HeadOffice' || role === 'SuperAdmin'
}

/**
 * Whether the link opens the audit on fewer stores than the widget shows: the dashboard's All stores,
 * for someone the audit shows only their own. {@link buildBucksAuditUrl} narrows exactly then, and the
 * link's tooltip says so from this same rule.
 */
export function auditNarrowsStores(storeId: string, chainWide: boolean): boolean {
    return storeId.trim().toLowerCase() === 'all' && !chainWide
}

export interface BucksAuditLinkContext {
    /** The dashboard's store selector: 'group' | 'all' | a store GUID. */
    storeId: string
    /** Inclusive yyyy-MM-dd bounds of the widget's figures (EmployeePerformanceData.startDate/endDate). */
    startDate: string
    endDate: string
    /** From {@link canAuditChainWide}. */
    chainWide: boolean
}

/**
 * The audit's Entries tab, filtered to this employee over the widget's dates and stores — or null
 * for the row of sales with no employee, which has nobody to look up.
 *
 * These are the filters the audit reads from its URL: tab, from/to (inclusive), storeId (absent for
 * My stores, 'all', or a store GUID) and employeeId, which only the Entries tab applies. Two
 * differences are the audit's, by design: it lists only flagged credits, and it counts a credit for
 * whoever APPLIED it on the day it was applied, where the widget counts every credit for whoever
 * RANG the sale, on the day of the sale.
 *
 * 'group' is the audit's My stores, its default. 'all' is All stores for someone the audit lets see
 * every store, and My stores — the widest view the audit gives them — for anyone else, rather than
 * a link that opens on a refusal. Ids are lower-cased to match the audit's store picker and rows.
 */
export function buildBucksAuditUrl(employeeId: string, ctx: BucksAuditLinkContext): string | null {
    const id = employeeId.trim().toLowerCase()
    if (!id || id === NO_EMPLOYEE_ID) return null
    const p = new URLSearchParams({ tab: 'entries', employeeId: id })
    if (ctx.startDate && ctx.endDate) {
        p.set('from', ctx.startDate)
        p.set('to', ctx.endDate)
    }
    const store = ctx.storeId.trim().toLowerCase()
    if (store === 'all') {
        if (!auditNarrowsStores(store, ctx.chainWide)) p.set('storeId', 'all')
    } else if (store && store !== 'group') {
        p.set('storeId', store)
    }
    return `/reports/adventure-bucks?${p.toString()}`
}

/** Initials for the avatar chip: first letters of the first and last words ("Mary-Ann van Dyke" → "MD"). */
export function employeeInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(w => /[\p{L}\p{N}]/u.test(w))
    if (words.length === 0) return '?'
    const first = words[0].match(/[\p{L}\p{N}]/u)?.[0] ?? ''
    const last = words.length > 1 ? (words[words.length - 1].match(/[\p{L}\p{N}]/u)?.[0] ?? '') : ''
    return (first + last).toUpperCase() || '?'
}
